import { fetchOperatorConnection, operatorRequestHeaders, OperatorConnection } from '../../../../providers/operatorConnection';

/**
 * One Operator's log files.
 *
 * **The Operator's half of this was rewritten on 2026-09-21 and these routes are not what they
 * were.** `/list` now carries each file's size and last write and is sorted most recently written
 * first; `/output` returns the **tail** rather than the whole file, because `end_node.log` on the
 * development box is 287 MB and the old route read all of it into the Operator process before
 * sending it; and `/clean` takes a flat `{file}` body, which is the first time it has worked at all
 * - it declared a `Communication` whose `payload` is a `str` while HQ sent an object, so every call
 * was refused with a 422 that HQ never looked at.
 *
 * All three answer plain JSON now instead of a JSON string inside a `message` field.
 *
 * A ticket is fetched per call, never held: it is good for about a minute and this panel sits open
 * far longer than that.
 */

export type LogFile = {
    name: string,
    /** Bytes, or null when the file could not be stated. Null is not zero. */
    size: number | null,
    /** ISO 8601, UTC, or null. */
    modified: string | null,
};

export type LogOutput = {
    file: string,
    output: string,
    /** The whole file's size, which is usually far larger than what was returned. */
    size: number | null,
    modified: string | null,
    /** How much came back, in bytes. After earlier chunks are added, how much is held in all. */
    returned: number,
    /**
     * Byte offsets in the file of the first and last byte held. `start` is what goes back to the
     * Operator as `before` to read the stretch above. Null from an Operator older than paging,
     * which is read as "cannot page" rather than as offset zero.
     */
    start: number | null,
    end: number | null,
    /** True when there is more above what was returned. */
    truncated: boolean,
};

/**
 * How much of a file to ask for, both on opening it and for each "Load more". The Operator
 * caps a single read at 2MB whatever is sent.
 */
export const TAIL_BYTES: number = 256 * 1024;

/**
 * The most this panel will hold. Everything loaded sits in one `<pre>`, and past a few tens of MB
 * the tab gets slow to scroll and to search. A reader who needs more than this wants the file
 * itself, which comes off the box by other means.
 */
export const MAX_HELD_BYTES: number = 20 * 1024 * 1024;

const text = (value: unknown): string => typeof value === 'string' ? value : '';

const countOrNull = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * A byte count an admin can read at a glance.
 *
 * The point of showing it at all is that one of these files is 287 MB and the others are a few KB,
 * and until now nothing on the screen said which was which.
 */
export const describeBytes = (bytes: number | null): string => {
    if (bytes === null) {
        return 'size unknown';
    }
    if (bytes < 1024) {
        return bytes + ' B';
    }
    // Compared after rounding, or 1,048,254 bytes reads as "1024 KB" rather than moving up a unit.
    if (Math.round(bytes / 1024) < 1024) {
        return Math.round(bytes / 1024) + ' KB';
    }
    if (bytes < 1024 * 1024 * 1024) {
        return (bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0) + ' MB';
    }

    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
};

/** Seconds since an ISO timestamp, or null if there was not one. */
export const secondsSince = (iso: string | null): number | null => {
    if (iso === null) {
        return null;
    }
    const then: number = Date.parse(iso);

    return Number.isNaN(then) ? null : Math.max(0, Math.round((Date.now() - then) / 1000));
};

/**
 * Ask the Operator, and turn anything that is not a 200 into a sentence worth showing.
 *
 * The Operator's own `detail.message` is written to be read by an admin - "That log file is not
 * there.", "Not a log file name." - so it is preferred over anything invented here.
 */
const ask = async (
    operatorId: number | string,
    path: string,
    init: RequestInit = { method: 'GET' }
): Promise<unknown> => {
    const connection: OperatorConnection = await fetchOperatorConnection(operatorId);
    const response: Response = await fetch(new Request(connection.host + path, {
        ...init,
        headers: operatorRequestHeaders(connection),
    }));

    let body: any = null;
    try {
        body = await response.json();
    } catch {
        body = null;
    }

    if (!response.ok) {
        const said: unknown = body?.detail?.message;

        throw new Error(typeof said === 'string' && said !== ''
            ? said
            : 'The Operator would not answer (' + response.status + ').');
    }

    return body;
};

/** Every log file on that Operator, most recently written first. */
export const loadLogFiles = async (operatorId: number | string): Promise<LogFile[]> => {
    const body = await ask(operatorId, '/system/logs/list') as Record<string, unknown>;

    return Array.isArray(body?.files)
        ? body.files.map((one): LogFile => {
            const file = (typeof one === 'object' && one !== null ? one : {}) as Record<string, unknown>;

            return { name: text(file.name), size: countOrNull(file.size), modified: text(file.modified) || null };
        })
        : [];
};

/**
 * The end of one log file, or with `before`, the stretch that ends at that byte offset: pass the
 * `start` of what is already held to read the part above it.
 */
export const loadLogOutput = async (
    operatorId: number | string,
    file: string,
    bytes: number = TAIL_BYTES,
    before: number | null = null
): Promise<LogOutput> => {
    const query: string = '?file=' + encodeURIComponent(file) + '&bytes_wanted=' + String(bytes)
        + (before === null ? '' : '&before=' + String(before));
    const body = await ask(operatorId, '/system/logs/output' + query) as Record<string, unknown>;

    return {
        file: text(body?.file) || file,
        output: text(body?.output),
        size: countOrNull(body?.size),
        modified: text(body?.modified) || null,
        returned: countOrNull(body?.returned) ?? 0,
        start: countOrNull(body?.start),
        end: countOrNull(body?.end),
        truncated: body?.truncated === true,
    };
};

/** Whether there is more above what is held, and the panel may still take it. */
export const canLoadEarlier = (held: LogOutput): boolean =>
    held.truncated && held.start !== null && held.start > 0 && held.returned < MAX_HELD_BYTES;

/**
 * Put an earlier chunk above what is held, or null when the two do not meet.
 *
 * **Null means the file changed underneath the reader**, almost always because it was emptied: the
 * Operator clamps a `before` past the end of the file, so the chunk ends somewhere other than where
 * the held text starts, and joining them would show a seam that is not in the file. The caller
 * should say so and read the tail again rather than guess. A chunk for a different file is refused
 * the same way.
 *
 * The size and last write stay those of the first read, not the earlier chunk's. The text held
 * ends where that first read ended, so a newer size would imply lines that are not on screen;
 * refreshing reads the tail again.
 */
export const prependEarlier = (held: LogOutput, earlier: LogOutput): LogOutput | null => {
    if (earlier.file !== held.file || held.start === null || earlier.end !== held.start) {
        return null;
    }

    return {
        ...held,
        output: earlier.output + held.output,
        returned: held.returned + earlier.returned,
        start: earlier.start,
        truncated: earlier.truncated,
    };
};

/** Empty one log file on the Operator. Destructive, and there is no undo. */
export const clearLogFile = async (operatorId: number | string, file: string): Promise<void> => {
    await ask(operatorId, '/system/logs/clean', {
        method: 'POST',
        body: JSON.stringify({ file }),
    });
};
